import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Loader } from 'components'
import type {
  ValidatePhase,
  ValidatePhaseEvent,
} from 'features/project-form/types'

type Props = {
  event: ValidatePhaseEvent | null
}

const DEFAULT_LABEL = 'Starting validation…'

const PHASE_LABELS: Record<ValidatePhase, string> = {
  hub_lookup: 'Looking up project context…',
  source_format: 'Checking source formatting…',
  source_keys_similarity: 'Comparing source column names…',
  source_values_similarity: 'Comparing source custom fields with AI…',
  source_db_keys_similarity:
    'Comparing source column names against your existing data…',
  source_db_values_similarity:
    'Comparing source values against your existing data…',
  source_dupe_urls: 'Checking for duplicate URLs…',
  product_format: 'Checking product formatting…',
  product_brands_similarity: 'Comparing product brands…',
  product_hierarchies_similarity: 'Comparing product hierarchies…',
  product_keys_similarity: 'Comparing product column names…',
  product_values_similarity: 'Comparing product custom fields with AI…',
  product_db_brands_similarity:
    'Comparing product brands against your existing data…',
  product_db_hierarchies_similarity:
    'Comparing product hierarchies against your existing data…',
  product_db_keys_similarity:
    'Comparing product column names against your existing data…',
  product_db_values_similarity:
    'Comparing product values against your existing data…',
  format_report: 'Generating validation report…',
  slack_upload: 'Uploading report to Slack…',
  done: 'Done.',
  error: 'Validation failed.',
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`
}

export function ValidationProgress({ event }: Props): JSX.Element {
  // Wall-clock elapsed, recomputed on every render so the value is fresh
  // regardless of whether the render was triggered by an SSE event or by
  // the interval below. Using a state-tracked `now` here caused the timer
  // to freeze during event bursts: each SSE event re-renders the component,
  // but the stale `now` state made elapsedMs look paused until the next
  // setInterval tick — and macrotasks like setInterval get starved while
  // the event loop drains the microtasks driving the `for await` consumer.
  const startedAtRef = useRef<number>(Date.now())
  const [, setTick] = useState(0)
  useEffect(() => {
    // Force a re-render every second so the timer keeps ticking during
    // quiet periods where no SSE events arrive (e.g., the long tail of a
    // single LLM call). The tick value is unused; the state update is the
    // signal.
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const elapsedMs = Date.now() - startedAtRef.current

  const label = event
    ? (PHASE_LABELS[event.phase] ?? event.phase)
    : DEFAULT_LABEL
  const showCounter =
    event &&
    typeof event.tasks_total === 'number' &&
    typeof event.tasks_done === 'number' &&
    event.tasks_total > 0
  // Counter only populates for LLM-batched phases, which run up to 50 calls
  // concurrently. Once tasks_total > 50 the counter visibly jumps in waves of
  // ~50 as each cohort settles together — surface that so users don't read
  // the steady-state pause as a stall.
  const showBatchHint = showCounter && event!.tasks_total! > 50

  return (
    <Container>
      <Loader />
      <Status>
        <PhaseLabel>{label}</PhaseLabel>
        <Meta>
          {showCounter && (
            <span>
              {event!.tasks_done} / {event!.tasks_total}
            </span>
          )}
          <span>{formatElapsed(elapsedMs)}</span>
        </Meta>
        {showBatchHint && (
          <Hint>
            Progress arrives in waves of 50 — LLM calls run in parallel batches.
          </Hint>
        )}
      </Status>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const Status = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #444;
  text-align: center;
`

const PhaseLabel = styled.div`
  font-weight: 500;
`

const Meta = styled.div`
  display: flex;
  gap: 8px;
  color: #888;
  font-variant-numeric: tabular-nums;

  > span:not(:last-child):after {
    content: '·';
    margin-left: 8px;
  }
`

const Hint = styled.div`
  font-size: 12px;
  color: #888;
  font-style: italic;
  max-width: 360px;
`
